import { useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import type { Plan } from '@localagent/sdk';

function formatPrice(cents: number): string {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(0)}/mo`;
}

export function PlanSelector({
  plans,
  currentPlanId,
  onChangePlan,
  isChanging,
}: {
  plans: Plan[];
  currentPlanId: string;
  onChangePlan: (planId: string) => void;
  isChanging: boolean;
}) {
  const [confirmingPlanId, setConfirmingPlanId] = useState<string | null>(null);
  const planKey = (p: Plan) => p.plan_id ?? p.id ?? '';

  const agentPlans = plans
    .filter((p) => p.is_active !== false && p.metadata?.tier_group === 'agent_billing' && planKey(p))
    .sort((a, b) => a.monthly_base_price_cents - b.monthly_base_price_cents);

  if (agentPlans.length === 0) return null;

  const currentIdx = agentPlans.findIndex((p) => planKey(p) === currentPlanId);

  return (
    <div className="rounded-lg bg-card p-4 space-y-3" data-testid="plan-selector">
      <h3 className="text-sm font-medium text-muted-foreground">Plans</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        {agentPlans.map((plan, idx) => {
          const id = planKey(plan);
          const isCurrent = id === currentPlanId;
          const action = idx > currentIdx ? 'Upgrade' : idx < currentIdx ? 'Downgrade' : null;
          const isConfirming = confirmingPlanId === id;

          return (
            <div
              key={id}
              className={`rounded-lg p-3 space-y-2 ${isCurrent ? 'bg-primary/10' : 'bg-background'}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{plan.display_name}</span>
                {isCurrent && (
                  <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Current</span>
                )}
              </div>
              <p className="text-lg font-semibold">{formatPrice(plan.monthly_base_price_cents)}</p>
              <p className="text-xs text-muted-foreground">
                {plan.included_credits.toLocaleString()} credits/month
              </p>
              {action && !isConfirming && (
                <Button
                  size="sm"
                  variant={action === 'Upgrade' ? 'default' : 'outline'}
                  className="w-full"
                  disabled={isChanging}
                  onClick={() => setConfirmingPlanId(id)}
                >
                  {action}
                </Button>
              )}
              {action && isConfirming && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1"
                    disabled={isChanging}
                    onClick={() => {
                      onChangePlan(id);
                      setConfirmingPlanId(null);
                    }}
                  >
                    {isChanging ? 'Changing...' : 'Confirm'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    disabled={isChanging}
                    onClick={() => setConfirmingPlanId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
